import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom, forkJoin } from 'rxjs';
import { AuthenticationService } from '@core/auth/authentication.service';
import { Modal } from '@shared/components/common/modal/modal';
import { FileInputComponent } from '@shared/components/form/file-input/file-input.component';
// import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { LabelComponent } from '@shared/components/form/label/label.component';
import { SelectFieldComponent, SelectOption } from '@shared/components/form/select/select-field.component';
// import { AppFormComponent } from '@shared/components/ui/list-components';
import { CreationButtons } from '@shared/components/ui/creation-buttons/creation-buttons';
import { ErrorService } from '@shared/services/error.service';
import { SnackbarService } from '@shared/services/snackbar.service';
import { hoyLocal } from '@shared/utils/fecha-local';
import { Cliente, Establecimiento } from '../../../crm/clientes/clientes.interface';
import { PlanillaReceptoras } from '../planilla-receptoras/planilla-receptoras';
import { AgendamientoTransferencia, ReceptoraAgendada, TecnicoTransferencia } from '../transferencias.interface';
import { ACCEPT_PROTOCOLO, EXTENSIONES_PROTOCOLO, opcionesTransferencistas } from '../transferencias.model';
import { TransferenciasService } from '../transferencias.service';
import {
  asignarReceptorasElegidas, LineaReceptora, ReceptorasPorEmbrion, ReceptoraTe, reusarLineas,
} from '../receptoras-por-embrion/receptoras-por-embrion';

export interface TransferirPajuelasData {
  clienteId: number;
  pajuelaIds: number[];
  /** Para elegir la receptora de cada embrión (2026-09-30): una línea por embrión de la pajuela. */
  pajuelas?: { id: number; etiqueta: string; embriones: number }[];
}

/**
 * "Transferir" desde el stock vitrificado: las pajuelas elegidas van a una TE
 * abierta del cliente o a una nueva, sin receptora (se completa en la planilla).
 */
@Component({
  selector: 'app-transferir-pajuelas',
  // 2026-09-30: FileInputComponent y PlanillaReceptoras eran del formulario de TE nueva (comentado).
  imports: [/* AppFormComponent, */ CreationButtons, /* FileInputComponent, InputFieldComponent, */ LabelComponent, Modal, /* PlanillaReceptoras, */ ReactiveFormsModule, ReceptorasPorEmbrion, SelectFieldComponent],
  templateUrl: './transferir-pajuelas.html',
})
export class TransferirPajuelas implements OnInit {
  abiertas: AgendamientoTransferencia[] = [];
  /** Receptora de cada embrión (2026-09-30): la lista de la TE elegida y las que ya tienen embrión. */
  lineas: LineaReceptora[] = [];
  receptorasTe: ReceptoraTe[] = [];
  ocupadas: number[] = [];
  cargandoTe = false;
  private previas = new Set<number>();
  establecimientos: Establecimiento[] = [];
  clientes: Cliente[] = [];
  tecnicos: TecnicoTransferencia[] = [];
  razas: { id: number; nombre: string }[] = [];
  pelajes: { id: number; nombre: string }[] = [];
  /** RP de las receptoras que ya tiene el establecimiento elegido; null hasta elegirlo. */
  rpsExistentes: string[] | null = null;
  readonly extensionesProtocolo = EXTENSIONES_PROTOCOLO;
  readonly acceptProtocolo = ACCEPT_PROTOCOLO;
  loading = false;
  guardando = false;

  readonly data = inject(MAT_DIALOG_DATA) as TransferirPajuelasData;
  /**
   * Cliente de la TE = dueño de las receptoras (2026-09-29). Arranca en el dueño de las
   * pajuelas, pero puede ser otro (capitalización, venta de embriones).
   */
  readonly clienteTe = new FormControl<string>(String(this.data.clienteId), { nonNullable: true });
  // 2026-09-30: la TE se agenda solo desde el calendario; acá siempre es una existente.
  // readonly destino = new FormControl<'existente' | 'nueva'>('nueva', { nonNullable: true });
  readonly destino = new FormControl<'existente' | 'nueva'>('existente', { nonNullable: true });
  readonly existente = new FormControl<string>('', { nonNullable: true });
  /** Opcional: con la hora cargada se piden los tres datos. */
  readonly descongelado = new FormGroup({
    fecha: new FormControl(hoyLocal(), { nonNullable: true }),
    hora: new FormControl('', { nonNullable: true }),
    hora_limite: new FormControl('', { nonNullable: true }),
  });
  /** "Nueva transferencia" crea el agendamiento de TE: los mismos campos que el alta desde el calendario (2026-09-29). */
  readonly nueva = new FormGroup({
    cli_establecimiento_id: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    fecha: new FormControl(hoyLocal(), { nonNullable: true, validators: Validators.required }),
    // hora: new FormControl('', { nonNullable: true }),
    fecha_fin: new FormControl('', { nonNullable: true }),
    cantidad_receptoras: new FormControl<number | null>(null),
    param_tecnico_transferencista_id: new FormControl('', { nonNullable: true }),
    protocolo: new FormControl<File | null>(null),
    receptoras: new FormControl<ReceptoraAgendada[]>([], { nonNullable: true }),
    param_raza_id: new FormControl('', { nonNullable: true }),
  });

  private service = inject(TransferenciasService);
  private auth = inject(AuthenticationService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private router = inject(Router);
  private dialogRef = inject(MatDialogRef<TransferirPajuelas>);

  /** Crear la TE es de Agendamientos; sin ese permiso solo se suma a una abierta. */
  readonly puedeCrear = this.auth.hasPermission('agendamientos.te', 'create');

  get abiertasOptions(): SelectOption[] {
    return this.abiertas.map(te => ({
      value: te.id,
      label: `${te.fecha.split('-').reverse().join('/')} · ${te.establecimiento?.nombre ?? ''} (${te.estado_agendamiento.nombre})`,
    }));
  }

  get clientesOptions(): SelectOption[] {
    return this.clientes.map(c => ({ value: c.id, label: c.persona.nombre_generico }));
  }

  get tecnicosOptions(): SelectOption[] {
    return opcionesTransferencistas(this.tecnicos);
  }

  get razasOptions(): SelectOption[] {
    return this.razas.map(r => ({ value: r.id, label: r.nombre }));
  }

  get establecimientosOptions(): SelectOption[] {
    return this.establecimientos.map(e => ({ value: e.id, label: e.nombre }));
  }

  get errorDescongelado(): string {
    // 2026-10-05: sin hora límite de uso, solo fecha y hora del descongelado.
    // const { fecha, hora, hora_limite } = this.descongelado.getRawValue();
    // if (!hora && !hora_limite) return '';
    // if (!fecha || !hora || !hora_limite) return 'Completá fecha, hora y hora límite del descongelado.';
    // if (hora_limite <= hora) return 'La hora límite tiene que ser posterior a la del descongelado.';
    const { fecha, hora } = this.descongelado.getRawValue();
    if (!hora) return '';
    if (!fecha) return 'Completá la fecha del descongelado.';
    return '';
  }

  get valido(): boolean {
    const destinoValido = this.destino.value === 'existente' ? !!this.existente.value : this.nueva.valid;
    // 2026-10-08: sin descongelado en este modal.
    // return destinoValido && !this.errorDescongelado;
    return destinoValido;
  }

  ngOnInit(): void {
    if (!this.puedeCrear) this.destino.setValue('existente');
    forkJoin([this.service.comboClientes(), this.service.comboTecnicosAgendamiento(), this.service.comboRazas(), this.service.comboPelajes()]).subscribe({
      next: ([clientes, tecnicos, razas, pelajes]) => {
        this.pelajes = pelajes.data;
        this.clientes = clientes.data;
        this.tecnicos = tecnicos.data;
        this.razas = razas.data;
        // Las receptoras nuevas nacen "Híbrida" (sin raza definida), si está.
        const hibrida = this.razas.find(r => r.nombre.toLowerCase().startsWith('híbrid'));
        if (hibrida) this.nueva.controls.param_raza_id.setValue(String(hibrida.id));
      },
      error: (error) => this.errorService.handleError(error),
    });
    this.nueva.controls.cli_establecimiento_id.valueChanges.subscribe(value => this.getReceptorasExistentes(value));
    this.clienteTe.valueChanges.subscribe(() => this.cargarDestinos());
    this.cargarDestinos();
    this.lineas = reusarLineas((this.data.pajuelas ?? []).flatMap(p =>
      Array.from({ length: Math.max(1, p.embriones) }, (_, i) => ({
        clave: `V-${p.id}-${i}`,
        fuente: `V-${p.id}`,
        etiqueta: p.embriones > 1 ? `${p.etiqueta} · ${i + 1}/${p.embriones}` : p.etiqueta,
      }))), []);
    this.existente.valueChanges.subscribe(id => this.cargarTe(Number(id)));
  }

  /** Receptoras de la TE elegida y cuáles ya tienen embrión; cambiar de TE borra lo elegido. */
  private cargarTe(id: number): void {
    this.lineas.forEach(l => l.receptora.setValue(''));
    this.receptorasTe = [];
    this.ocupadas = [];
    this.previas = new Set();
    if (!id) return;
    this.cargandoTe = true;
    forkJoin([this.service.show(id), this.service.indexAsignaciones(id)]).subscribe({
      next: ([te, asignaciones]) => {
        this.receptorasTe = te.data.receptoras ?? [];
        this.ocupadas = asignaciones.data.map(a => a.ani_animal_receptora_id).filter((r): r is number => !!r);
        this.previas = new Set(asignaciones.data.map(a => a.id));
      },
      error: (error) => this.errorService.handleError(error),
    }).add(() => { this.cargandoTe = false; });
  }

  private cargarDestinos(): void {
    const clienteId = Number(this.clienteTe.value);
    this.existente.setValue('');
    this.nueva.controls.cli_establecimiento_id.setValue('');
    if (!clienteId) return;
    this.loading = true;
    forkJoin([
      this.service.index({ cli_cliente_id: clienteId }),
      this.service.comboEstablecimientos(clienteId),
    ]).subscribe({
      next: ([transferencias, establecimientos]) => {
        // Se pueden sumar embriones mientras la TE no está finalizada ni anulada.
        this.abiertas = transferencias.data.filter(te => ['P', 'G'].includes(te.estado_agendamiento.codigo));
        this.establecimientos = establecimientos.data;
        // 2026-09-29: primero se ofrece sumar a una TE abierta; "Nueva" solo si no hay ninguna.
        // this.destino.setValue(this.abiertas.length || !this.puedeCrear ? 'existente' : 'nueva');
        // if (this.abiertas.length === 1 || (this.abiertas.length && !this.puedeCrear)) this.existente.setValue(String(this.abiertas[0].id));
        if (this.abiertas.length === 1) this.existente.setValue(String(this.abiertas[0].id));
      },
      error: (error) => this.errorService.handleError(error),
    }).add(() => { this.loading = false; });
  }

  private getReceptorasExistentes(establecimientoId: string): void {
    this.rpsExistentes = null;
    if (!establecimientoId) return;
    this.service.comboReceptorasEstablecimiento(Number(establecimientoId)).subscribe({
      next: (response) => { this.rpsExistentes = response.data.map(r => r.rp); },
      error: (error) => this.errorService.handleError(error),
    });
  }

  async transferir(): Promise<void> {
    this.guardando = true;
    try {
      const transferenciaId = Number(this.existente.value);
      // 2026-09-30: la TE se agenda solo desde el calendario.
      /*
      if (this.destino.value === 'nueva') {
        const raw = this.nueva.getRawValue();
        const datos = {
          cli_establecimiento_id: Number(raw.cli_establecimiento_id),
          fecha: raw.fecha,
          fecha_fin: raw.fecha_fin || null,
          cantidad_receptoras: raw.cantidad_receptoras || null,
          param_tecnico_transferencista_id: raw.param_tecnico_transferencista_id ? Number(raw.param_tecnico_transferencista_id) : null,
        };
        const creada = await firstValueFrom(this.service.store(raw.receptoras.length
          ? { ...datos, receptoras: raw.receptoras, param_raza_id: raw.param_raza_id ? Number(raw.param_raza_id) : null }
          : datos));
        transferenciaId = creada.data.id;
        // El protocolo va por su propio endpoint; si falla, la TE ya existe y las pajuelas se cargan igual.
        if (raw.protocolo) {
          await firstValueFrom(this.service.subirProtocolo(transferenciaId, raw.protocolo))
            .catch(error => { if (error instanceof HttpErrorResponse) this.errorService.handleError(error); });
        }
      }
      */

      // 2026-10-08: el descongelado se carga por embrión en la planilla de TE.
      // const descongelado = this.descongelado.getRawValue();
      const lote = await firstValueFrom(this.service.storeAsignacionesLote(transferenciaId, this.data.pajuelaIds.map(id => ({
        fuente_tipo: 'V' as const,
        sto_stock_vitrificado_embrion_id: id,
      }))/* , descongelado.hora ? descongelado : null */));
      // 2026-09-30: la receptora elegida para cada embrión (queda Asignada).
      await asignarReceptorasElegidas(this.service, transferenciaId, this.previas, lote.data, this.lineas);

      this.snackbarService.showSnackBar(`${this.data.pajuelaIds.length} pajuela(s) agregada(s) a la transferencia`);
      this.dialogRef.close(transferenciaId);
      // 2026-10-08: quien asigna desde Stock puede no tener acceso a TE; entonces se queda en Stock.
      if (this.auth.canAccessModule('te')) this.router.navigate(['/te', transferenciaId]);
    } catch (error) {
      if (error instanceof HttpErrorResponse) this.errorService.handleError(error);
    } finally {
      this.guardando = false;
    }
  }

  cancelar(): void {
    this.dialogRef.close();
  }
}
